import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import { Fade } from "react-awesome-reveal";
import "swiper/css";

import prev from "../../img/home/prev.svg";
import next from "../../img/home/next.svg";

import SectionTitle from "../home/SectionTitle";
import SliderLine from "../home/SliderLine";

import startImg from "../../img/accounts/card-start.webp";
import edgeImg from "../../img/accounts/card-edge.webp";
import primeImg from "../../img/accounts/card-prime.webp";
import eliteImg from "../../img/accounts/card-elite.webp";
import moreImg from "../../img/accounts/card-more.webp";

const CARDS = [
  { img: startImg, badgeKey: "accountsPage.levels.tier1.name", textKey: "accountsPage.ladder.tier1.text" },
  { img: edgeImg, badgeKey: "accountsPage.levels.tier2.name", textKey: "accountsPage.ladder.tier2.text" },
  { img: primeImg, badgeKey: "accountsPage.levels.tier3.name", textKey: "accountsPage.ladder.tier3.text" },
  { img: eliteImg, badgeKey: "accountsPage.levels.tier4.name", textKey: "accountsPage.ladder.tier4.text" },
  { img: moreImg, badgeKey: "accountsPage.levels.tier5.name", textKey: "accountsPage.ladder.tier5.text" },
];

// Силуэт ряда из макета: высоты/сдвиги привязаны к КОЛОНКЕ, а не к карточке —
// карточка морфится под позицию, когда приезжает на неё
const COLUMN_PATTERN = [
  { h: 448, o: 0 },
  { h: 316, o: 130 },
  { h: 316, o: 0 },
  { h: 448, o: 0 },
];

// Позиция для дробной колонки (во время свайпа пальцем) — линейная интерполяция
function columnPos(f) {
  const at = (k) => COLUMN_PATTERN[Math.min(COLUMN_PATTERN.length - 1, Math.max(0, k))];
  const k = Math.floor(f);
  const a = at(k);
  const b = at(k + 1);
  const t = f - k;
  return { h: a.h + (b.h - a.h) * t, o: a.o + (b.o - a.o) * t };
}

function applyColumnHeights(swiper) {
  swiper.slides.forEach((slideEl) => {
    const card = slideEl.firstElementChild;
    if (!card) return;
    const pos = columnPos(-slideEl.progress);
    card.style.setProperty("--card-h", `${pos.h}px`);
    card.style.setProperty("--card-offset", `${pos.o}px`);
  });
}

function LadderCard({ item, t }) {
  return (
    <div className="ladder-card relative overflow-hidden rounded-[20px] bg-[#f2f2f1] h-[316px] sm:h-[360px] lg:h-[var(--card-h,448px)] lg:mt-[var(--card-offset,0px)] lg:transition-[height,margin-top] lg:ease-out">
      <img
        src={item.img}
        alt=""
        aria-hidden
        className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
      />

      <div
        className="absolute left-[13px] right-[13px] bottom-[13px] rounded-[15px] p-[12px] flex flex-col gap-[15px]"
        style={{ background: "rgba(255,255,255,0.9)", backdropFilter: "blur(6px)" }}
      >
        <div className="bg-[#bfccd7] inline-flex items-center px-[18px] py-[6px] rounded-full self-start">
          <span className="font-['Inter'] font-normal text-[#1c385f] text-[17px] lg:text-[21px] uppercase">
            {t(item.badgeKey)}
          </span>
        </div>
        <ul>
          <li className="font-['Inter'] font-normal text-[15px] lg:text-[18px] leading-[1.5] text-[#1c385f] list-disc ml-[20px]">
            {t(item.textKey)}
          </li>
        </ul>
      </div>
    </div>
  );
}

function AccessLadder() {
  const { t } = useTranslation();
  const swiperRef = useRef(null);
  const [current, setCurrent] = useState(0);

  return (
    <section className="mt">
      <div className="glcontainer">
        <SectionTitle
          title1={t("accountsPage.ladder.title1")}
          title2={t("accountsPage.ladder.title2")}
          text={t("accountsPage.ladder.subtitle")}
          className="mb-[35px] lg:mb-[50px] mx-auto max-w-[528px]"
        />

        <Fade triggerOnce direction="up" duration={700}>
          <div className="relative">
            <Swiper
              modules={[Autoplay]}
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
                applyColumnHeights(swiper);
              }}
              onSlideChange={(swiper) => setCurrent(swiper.realIndex % CARDS.length)}
              onProgress={applyColumnHeights}
              onSetTransition={(swiper, duration) => {
                swiper.slides.forEach((slideEl) => {
                  const card = slideEl.firstElementChild;
                  if (card) card.style.transitionDuration = `${duration}ms`;
                });
              }}
              watchSlidesProgress
              spaceBetween={18}
              slidesPerView={1}
              loop
              speed={800}
              autoplay={{
                delay: 4000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }}
              grabCursor
              breakpoints={{
                640: { slidesPerView: 2.15, spaceBetween: 20 },
                1024: { slidesPerView: 4, spaceBetween: 30 },
              }}
            >
              {[...CARDS, ...CARDS].map((item, i) => (
                <SwiperSlide key={`${item.badgeKey}-${i}`}>
                  <LadderCard item={item} t={t} />
                </SwiperSlide>
              ))}
            </Swiper>

            {/* Десктопные стрелки */}
            <div className="hidden lg:flex absolute bottom-0 right-[calc(25%+8px)] z-10 items-center gap-[20px]">
              <button
                onClick={() => swiperRef.current?.slidePrev()}
                className="hover:scale-[1.05] transition-all"
                aria-label="Previous"
              >
                <img src={prev} alt="Previous" />
              </button>
              <button
                onClick={() => swiperRef.current?.slideNext()}
                className="hover:scale-[1.05] transition-all"
                aria-label="Next"
              >
                <img src={next} alt="Next" />
              </button>
            </div>
          </div>
        </Fade>

        {/* Планшетные стрелки — по центру под слайдером */}
        <div className="hidden sm:flex lg:hidden mt-[28px] items-center justify-center gap-[20px]">
          <button
            onClick={() => swiperRef.current?.slidePrev()}
            className="w-[52px] h-[52px] rounded-full border border-[#1c385f] flex items-center justify-center text-[#1c385f] hover:bg-[#1c385f] hover:text-white transition-all duration-200"
            aria-label="Previous"
          >
            <ArrowLeft strokeWidth={1.5} className="w-[24px] h-[24px]" />
          </button>
          <button
            onClick={() => swiperRef.current?.slideNext()}
            className="w-[52px] h-[52px] rounded-full border border-[#1c385f] flex items-center justify-center text-[#1c385f] hover:bg-[#1c385f] hover:text-white transition-all duration-200"
            aria-label="Next"
          >
            <ArrowRight strokeWidth={1.5} className="w-[24px] h-[24px]" />
          </button>
        </div>

        {/* Мобильная пагинация */}
        <div className="sm:hidden">
          <SliderLine
            current={current}
            total={CARDS.length}
            onPrev={() => swiperRef.current?.slidePrev()}
            onNext={() => swiperRef.current?.slideNext()}
          />
        </div>
      </div>
    </section>
  );
}

export default AccessLadder;
